import type { Vec2, Ring } from '../../../geo/types';
import type { Rng } from '../../../core/rng';
import type { Frame, MeshBuilder } from '../../mesh/meshBuilder';
import { horizontalCap, prismSolid, pyramidOverRing, ringPoints } from '../solids';
import { gableRoof, hipRoof } from '../roofSolids';
import { emitFacade, type FacadeContext } from '../houseFacades';
import { edgeFace, type MasonryStyles } from './masonry';
import { civicGable, civicOpenings, type ClearRect } from './civicOpenings';

export type BlockRoof = 'hip' | 'gable' | 'flat' | 'cone';

export interface PlanRect {
  readonly u0: number;
  readonly u1: number;
  readonly v0: number;
  readonly v1: number;
}

export interface BlockSpec {
  readonly eaveY: number;
  readonly roof: BlockRoof;
  readonly pitch: number;
}

/**
 * What a civic block needs to open its walls: the facade styles and detail sink, a stream for
 * the door bay, and which wall of the block, counted like `corners`, has the entrance (-1: none).
 */
export interface CivicContext {
  readonly facade: FacadeContext;
  readonly rng: Rng;
  readonly doorWall: number;
  /** A building type's own entrance wall, built in place of a wall with a house door. */
  readonly entrance?: EntranceWall;
}

/** Builds the entrance wall along `edge`, fitted `styles`, up to the eaves or to `apexY` on a gable end. */
export type EntranceWall = (
  civic: CivicContext,
  styles: MasonryStyles,
  edge: readonly [Vec2, Vec2],
  spec: BlockSpec,
  apexY: number | null
) => void;

export const BLOCK = {
  coneRiseRatio: 1.1,
  courtyardShare: 0.3,
  maxRangeWidth: 9,
  minCourtyard: 4,
} as const;

/** Wall styles whose facade extent matches one wall. */
function fitted(styles: MasonryStyles, width: number, eaveY: number): MasonryStyles {
  return { ...styles, wall: { ...styles.wall, extent: [width, eaveY] } };
}

/** Plan corners counter-clockwise from (u0, v0); wall i runs from corner i to corner i + 1. */
export function corners(r: PlanRect): [Vec2, Vec2, Vec2, Vec2] {
  return [
    [r.u0, r.v0],
    [r.u1, r.v0],
    [r.u1, r.v1],
    [r.u0, r.v1],
  ];
}

/** Outward plan normals of the walls of a rectangle, in `corners` order. */
export const WALL_NORMALS: readonly Vec2[] = [
  [0, -1],
  [1, 0],
  [0, 1],
  [-1, 0],
];

interface WallPlan {
  readonly index: number;
  readonly apexY: number | null;
  readonly keepClear: readonly ClearRect[];
}

function openWall(civic: CivicContext, styles: MasonryStyles, edge: readonly [Vec2, Vec2], spec: BlockSpec, wall: WallPlan): void {
  const length = Math.hypot(edge[1][0] - edge[0][0], edge[1][1] - edge[0][1]);
  const eaveY = spec.eaveY;
  if (wall.index === civic.doorWall && civic.entrance) {
    civic.entrance(civic, fitted(styles, length, eaveY), edge, spec, wall.apexY);
    return;
  }
  const openings = civicOpenings(
    { length, y0: 0, y1: eaveY, eaveY, door: wall.index === civic.doorWall, keepClear: wall.keepClear },
    civic.rng.fork(`wall:${wall.index}`)
  );
  const ctx: FacadeContext = { ...civic.facade, styles: { ...civic.facade.styles, wall: fitted(styles, length, eaveY).wall } };
  const size = { width: length, eaves: eaveY, ridge: wall.apexY ?? eaveY };
  const gable = wall.apexY === null ? null : civicGable(length, eaveY, spec.pitch);
  emitFacade(ctx, edgeFace(edge[0], edge[1]), size, { wall: openings, gable });
}

function gabledBlock(styles: MasonryStyles, civic: CivicContext, r: PlanRect, spec: BlockSpec, covered: readonly ClearRect[][]): void {
  const { builder, frame } = civic.facade;
  const alongU = r.u1 - r.u0 >= r.v1 - r.v0;
  const span = alongU ? r.v1 - r.v0 : r.u1 - r.u0;
  const ridgeY = spec.eaveY + (span / 2) * Math.tan(spec.pitch);
  const c = corners(r);
  c.forEach((from, i) => {
    const isEnd = alongU ? i % 2 === 1 : i % 2 === 0;
    openWall(civic, styles, [from, c[(i + 1) % 4]!], spec, { index: i, apexY: isEnd ? ridgeY : null, keepClear: covered[i] ?? [] });
  });
  const ends = { start: false, end: false };
  if (alongU) {
    gableRoof(builder, styles, frame, { ...r, eaveY: spec.eaveY, ridgeY, ends });
  } else {
    const turned = { u0: r.v0, u1: r.v1, v0: -r.u1, v1: -r.u0 };
    gableRoof(builder, styles, { origin: frame.origin, yaw: frame.yaw + Math.PI / 2 }, { ...turned, eaveY: spec.eaveY, ridgeY, ends });
  }
}

/**
 * Rectangular block with its walls opened to the eaves and a hip, gable or flat roof. `covered`
 * holds, per wall, the stretches another part of the building stands against.
 */
export function buildRectBlock(
  styles: MasonryStyles,
  civic: CivicContext,
  r: PlanRect,
  spec: BlockSpec,
  covered: readonly ClearRect[][] = []
): void {
  if (spec.roof === 'gable') {
    gabledBlock(styles, civic, r, spec, covered);
    return;
  }
  const { builder, frame } = civic.facade;
  const c = corners(r);
  c.forEach((from, i) => {
    openWall(civic, styles, [from, c[(i + 1) % 4]!], spec, { index: i, apexY: null, keepClear: covered[i] ?? [] });
  });
  if (spec.roof === 'flat') horizontalCap(builder, styles.roof, frame, c, spec.eaveY, true);
  else hipRoof(builder, styles.roof, frame, { ...r, eaveY: spec.eaveY, pitch: spec.pitch });
}

/** Round, octagonal or square body under a cone, pyramid or flat top. */
export function buildRadialBlock(
  builder: MeshBuilder,
  styles: MasonryStyles,
  frame: Frame,
  ring: { readonly center: Vec2; readonly radius: number; readonly segments: number; readonly phase: number },
  spec: BlockSpec
): void {
  const base: Ring = ringPoints(ring.center, ring.radius, ring.segments, ring.phase);
  const flat = spec.roof === 'flat';
  prismSolid(builder, styles.wall, frame, base, 0, spec.eaveY, { top: flat, bottom: false });
  if (flat) return;
  const rise = spec.roof === 'cone' ? BLOCK.coneRiseRatio * ring.radius : ring.radius * Math.tan(spec.pitch);
  pyramidOverRing(builder, styles, frame, base, spec.eaveY, { plan: ring.center, y: spec.eaveY + rise });
}

/** Where wall `i` of `range` has another range standing against it, in wall coordinates. */
function coveredStretches(range: PlanRect, i: number, others: readonly PlanRect[], height: number): ClearRect[] {
  const spans = others.flatMap((q): [number, number][] => {
    if (i === 0 && q.v1 === range.v0) return [[Math.max(q.u0, range.u0) - range.u0, Math.min(q.u1, range.u1) - range.u0]];
    if (i === 2 && q.v0 === range.v1) return [[range.u1 - Math.min(q.u1, range.u1), range.u1 - Math.max(q.u0, range.u0)]];
    if (i === 1 && q.u0 === range.u1) return [[Math.max(q.v0, range.v0) - range.v0, Math.min(q.v1, range.v1) - range.v0]];
    if (i === 3 && q.u1 === range.u0) return [[range.v1 - Math.min(q.v1, range.v1), range.v1 - Math.max(q.v0, range.v0)]];
    return [];
  });
  return spans.filter(([a, b]) => b > a).map(([x0, x1]) => ({ x0, x1, y0: 0, y1: height }));
}

/** Range and wall of a courtyard that carry the outer wall `i` of the whole block. */
const OUTER_WALL: readonly (readonly [number, number])[] = [
  [0, 0],
  [3, 1],
  [1, 2],
  [2, 3],
];

/** Four hipped ranges around an open courtyard, or one block when there is no room for one. */
export function buildCourtyard(styles: MasonryStyles, civic: CivicContext, r: PlanRect, spec: BlockSpec): void {
  const short = Math.min(r.u1 - r.u0, r.v1 - r.v0);
  const w = Math.min(BLOCK.maxRangeWidth, BLOCK.courtyardShare * short);
  if (short - 2 * w < BLOCK.minCourtyard) {
    buildRectBlock(styles, civic, r, spec);
    return;
  }
  const ranges: PlanRect[] = [
    { ...r, v1: r.v0 + w },
    { ...r, v0: r.v1 - w },
    { u0: r.u0, u1: r.u0 + w, v0: r.v0 + w, v1: r.v1 - w },
    { u0: r.u1 - w, u1: r.u1, v0: r.v0 + w, v1: r.v1 - w },
  ];
  const door = OUTER_WALL[civic.doorWall];
  ranges.forEach((range, k) => {
    const others = ranges.filter((_, j) => j !== k);
    const covered = [0, 1, 2, 3].map((i) => coveredStretches(range, i, others, spec.eaveY));
    const doorWall = door && door[0] === k ? door[1] : -1;
    buildRectBlock(styles, { ...civic, rng: civic.rng.fork(`range:${k}`), doorWall }, range, { ...spec, roof: 'hip' }, covered);
  });
}
